export type CampaignTemplateRender = {
  imageUrl?: string;
  body: string;
  footerCode?: string;
};

const CAMPAIGN_PLACEHOLDER_REGEX = /^Campanha:\s*(.+?)\s*\((.+)\)\s*$/i;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function getIgnoreCase(obj: Record<string, unknown>, ...keys: string[]): unknown {
  const map = new Map(Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value]));
  for (const key of keys) {
    if (map.has(key.toLowerCase())) {
      return map.get(key.toLowerCase());
    }
  }
  return undefined;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function componentType(component: Record<string, unknown>): string {
  return asString(getIgnoreCase(component, "type")).toLowerCase();
}

function getComponents(root: Record<string, unknown>): Record<string, unknown>[] {
  const template = asRecord(getIgnoreCase(root, "template")) ?? root;
  return asArray(getIgnoreCase(template, "components"))
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null);
}

function parameterTexts(component: Record<string, unknown>): string[] {
  return asArray(getIgnoreCase(component, "parameters"))
    .map((parameter) => {
      const record = asRecord(parameter);
      if (!record) return "";
      return (
        asString(getIgnoreCase(record, "text")) ||
        asString(getIgnoreCase(record, "payload")) ||
        asString(getIgnoreCase(asRecord(getIgnoreCase(record, "image")) ?? {}, "link"))
      );
    })
    .filter(Boolean);
}

function extractImageUrl(components: Record<string, unknown>[]): string | undefined {
  for (const component of components) {
    if (componentType(component) !== "header") continue;

    const parameters = asArray(getIgnoreCase(component, "parameters"));
    for (const parameter of parameters) {
      const record = asRecord(parameter);
      if (!record) continue;
      const image = asRecord(getIgnoreCase(record, "image"));
      const link =
        asString(getIgnoreCase(image ?? {}, "link")) ||
        asString(getIgnoreCase(image ?? {}, "url"));
      if (/^https?:\/\//i.test(link)) return link;
    }

    const example = asRecord(getIgnoreCase(component, "example"));
    const headerHandle = asArray(getIgnoreCase(example ?? {}, "header_handle", "headerHandle"));
    const firstHandle = headerHandle.find((item) => typeof item === "string" && item) as
      | string
      | undefined;
    if (firstHandle) return firstHandle;
  }

  return undefined;
}

function looksLikeMessageBody(params: string[]): boolean {
  if (params.length === 0) return false;
  if (params.length === 1) return params[0].includes("\n") || params[0].length > 40;
  return params.some((text) => text.includes("\n") || text.length > 80);
}

function extractBody(components: Record<string, unknown>[]): string {
  for (const component of components) {
    if (componentType(component) !== "body") continue;

    const params = parameterTexts(component).filter((text) => !/^https?:\/\//i.test(text));
    const text = asString(getIgnoreCase(component, "text"));

    if (text.trim()) {
      return text.replace(/\{\{(\d+)\}\}/g, (_, index: string) => {
        return params[Number(index) - 1] ?? "";
      });
    }

    if (looksLikeMessageBody(params)) {
      return params.join("\n");
    }
  }

  return "";
}

function extractFooterCodes(components: Record<string, unknown>[]): string[] {
  const labels: string[] = [];

  for (const component of components) {
    const type = componentType(component);
    if (type !== "button" && type !== "buttons") continue;

    const buttons = asArray(getIgnoreCase(component, "buttons"));
    for (const button of buttons) {
      const record = asRecord(button);
      if (!record) continue;
      const text = asString(getIgnoreCase(record, "text"));
      if (text) labels.push(text);
    }

    for (const text of parameterTexts(component)) {
      if (text && !/^https?:\/\//i.test(text)) {
        labels.push(text);
      }
    }
  }

  return labels;
}

export function extractCampaignCodeFromPlaceholder(text?: string | null): string | undefined {
  if (!text) return undefined;
  const match = text.trim().match(CAMPAIGN_PLACEHOLDER_REGEX);
  return match?.[1]?.trim() || undefined;
}

export function extractCampaignNameFromPlaceholder(text?: string | null): string | undefined {
  if (!text) return undefined;
  const match = text.trim().match(CAMPAIGN_PLACEHOLDER_REGEX);
  return match?.[2]?.trim() || undefined;
}

export function isCampaignPlaceholder(text?: string | null): boolean {
  return Boolean(extractCampaignCodeFromPlaceholder(text));
}

export function interpolateCampaignText(
  text: string | null | undefined,
  values: { code?: string | null; name?: string | null; discount?: string | number | null }
): string {
  if (!text) return "";

  const discount =
    values.discount === null || values.discount === undefined || values.discount === ""
      ? ""
      : String(values.discount);

  return text
    .replace(/#CAMPAIGNCODE#/gi, values.code || "")
    .replace(/#CAMPAIGNNAME#/gi, values.name || "")
    .replace(/#DISCOUNT#/gi, discount)
    .replace(/#[A-Z_]+#/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function parseTemplateJson(templateText: string | null | undefined): Record<string, unknown> | null {
  if (!templateText || typeof templateText !== "string" || !templateText.trim()) return null;

  try {
    const parsed = JSON.parse(templateText);
    const parsedRoot = asRecord(parsed);
    if (!parsedRoot) return null;

    const nestedTemplateText = getIgnoreCase(parsedRoot, "TemplateText", "templateText");
    if (typeof nestedTemplateText === "string") {
      try {
        const nested = asRecord(JSON.parse(nestedTemplateText));
        if (nested) return nested;
      } catch {
        return parsedRoot;
      }
    }

    return parsedRoot;
  } catch {
    return null;
  }
}

function extractHttpImageFromRaw(raw: string): string | undefined {
  const match = raw.match(/"link"\s*:\s*"(https?:\/\/[^"]+)"/i);
  return match?.[1];
}

export function parseCampaignTemplate(
  templateText: string | null | undefined,
  fallbackCode?: string
): CampaignTemplateRender | null {
  if (!templateText || typeof templateText !== "string" || !templateText.trim()) return null;

  const root = parseTemplateJson(templateText);
  const looseImage = extractHttpImageFromRaw(templateText);

  if (!root) {
    if (!looseImage && !fallbackCode) return null;
    return {
      imageUrl: looseImage,
      body: "",
      footerCode: fallbackCode,
    };
  }

  const components = getComponents(root);
  const imageUrl = extractImageUrl(components) || looseImage;
  const body = extractBody(components).trim();
  const footerCode = extractFooterCodes(components)[0] || fallbackCode;

  if (!imageUrl && !body && !footerCode) return null;

  return {
    imageUrl,
    body,
    footerCode,
  };
}

export type CampaignRenderInput = {
  templateText?: string | null;
  placeholderText?: string | null;
  campaignImageUrl?: string | null;
  campaignText?: string | null;
  campaignCode?: string | null;
  campaignDiscountPercentage?: number | null;
};

export function buildCampaignRender(input: CampaignRenderInput): CampaignTemplateRender | null {
  const placeholderCode = extractCampaignCodeFromPlaceholder(input.placeholderText);
  const placeholderName = extractCampaignNameFromPlaceholder(input.placeholderText);
  const code = input.campaignCode || placeholderCode;
  const parsed = parseCampaignTemplate(input.templateText, code || undefined);
  const interpolatedBody = interpolateCampaignText(input.campaignText, {
    code,
    name: placeholderName,
    discount: input.campaignDiscountPercentage,
  });

  const imageUrl = parsed?.imageUrl || input.campaignImageUrl || undefined;
  const body = parsed?.body || interpolatedBody;
  const footerCode = parsed?.footerCode || code || undefined;

  if (!imageUrl && !body && !footerCode) return null;
  if (!imageUrl && !body && isCampaignPlaceholder(input.placeholderText) && !input.campaignText) {
    return null;
  }

  return {
    imageUrl,
    body,
    footerCode,
  };
}
